import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import { useAdminCompanies } from 'app/javascript/queries/useAdmin';
import { useDebouncedValue } from 'app/javascript/hooks/useDebouncedValue';
import AdminPanel from 'app/javascript/components/admin/AdminPanel';
import AdminPagination from 'app/javascript/components/admin/AdminPagination';
import AdminSearchField from 'app/javascript/components/admin/AdminSearchField';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'app/javascript/components/ui/table';
import { ROUTES } from 'app/javascript/constants/routes';
import { formatBRDateTime } from 'app/javascript/utils/dates';

export default function RecentCompaniesTable() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading, isError } = useAdminCompanies({ search: debouncedSearch, page });
  const companies = data?.companies ?? [];

  // A new query invalidates the current page number — page 3 of the old result
  // set means nothing in the new one.
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className="space-y-3">
      <AdminSearchField
        value={search}
        onChange={handleSearch}
        placeholder="Buscar por nome, slug ou cidade"
        resultCount={data?.pagy.count}
      />
      <AdminPanel
        isLoading={isLoading}
        isError={isError}
        isEmpty={companies.length === 0}
        emptyMessage={
          debouncedSearch
            ? 'Nenhuma academia encontrada para esta busca.'
            : 'Nenhuma academia criada ainda.'
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Dono</TableHead>
              <TableHead className="text-right">Membros</TableHead>
              <TableHead>Criada em</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.map((company) => (
              <TableRow key={company.id}>
                <TableCell className="font-medium">
                  <span className="block">{company.name}</span>
                  {/* New tab on purpose: the public profile is what the academy's
                      visitors see, checked side by side with the admin list. */}
                  <Link
                    to={ROUTES.COMPANY_DETAILS}
                    params={{ slug: company.slug }}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block text-xs text-muted-foreground hover:text-foreground hover:underline"
                  >
                    /bjj/{company.slug}
                  </Link>
                </TableCell>
                <TableCell>
                  {company.owner_name ? (
                    <>
                      <span className="block">{company.owner_name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {company.owner_email}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">{company.member_count}</TableCell>
                <TableCell className="text-muted-foreground">
                  {formatBRDateTime(company.created_at)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-3">
                    <Link
                      to={ROUTES.ADMIN_COMPANY}
                      params={{ slug: company.slug }}
                      className="inline-flex items-center gap-1.5 text-sm text-yellow-400 hover:underline"
                    >
                      <Activity className="h-3.5 w-3.5" />
                      Saúde
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AdminPanel>
      <AdminPagination pagy={data?.pagy} onPageChange={setPage} />
    </div>
  );
}
